import { StatusId, OperationBC, CommandeRang, OperationFormulaire } from '../types';
import { Article } from '../../../services/articlesService';
import { isValidEmail, isValidPhoneFr, FORMAT_ERROR_MAIL, FORMAT_ERROR_TELEPHONE } from '../../../utils/formatValidation';

// Derivations PURES de l'onglet Consultation acheteur-BC : formatage des articles,
// lecture seule, statut de consultation, validation, corps de sauvegarde. Aucune
// dependance React. Gating par id de statut.

const parseRang = (value: string | number): number => parseInt(String(value), 10);

const getCmd = (operation: OperationBC, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

const isBrouillon = (operation: OperationBC): boolean =>
  operation.status?.id === StatusId.BrouillonAcheteur;

// Mapping champ API (snake_case) -> champ formulaire (camelCase) pour remapper les
// erreurs 422. Source unique (cf. ancienne API_TO_FORM_FIELD_BC).
//   facturation -> modaliteFacturation ; coordonnees_maitrise_ouvrage -> coordonneesMaitrise.
export const API_TO_FORM_FIELD_BC: Record<string, string> = {
  nom: 'nomOperation',
  reference: 'referenceInterne',
  besoin: 'besoin',
  adresse: 'adresse',
  code_postal: 'codePostal',
  ville: 'ville',
  facturation: 'modaliteFacturation',
  coordonnees_maitrise_ouvrage: 'coordonneesMaitrise',
  condition_visite: 'conditionVisite',
};

export const mapApiErrorsToForm = (errors: Record<string, string[]>): Record<string, string> => {
  const mapped: Record<string, string> = {};
  Object.keys(errors).forEach((field) => {
    const messages = errors[field];
    if (Array.isArray(messages) && messages.length > 0) {
      mapped[API_TO_FORM_FIELD_BC[field] || field] = messages[0];
    }
  });
  return mapped;
};

// --- Articles existants (tableau financier acheteur, mode brouillon ou lecture) -
export const formatExistingArticles = (operation: OperationBC, rang: number): Article[] => {
  const cmd = getCmd(operation, rang);
  const articles = cmd?.articles || [];

  return articles.map((item): Article => {
    const catalogue = item.catalogue;
    const articleData = catalogue?.article;

    const departementId = operation.lot_departement?.departement?.id || '00';
    const lotTechniqueId = operation.lot_technique?.id || '000';
    const code = catalogue?.code || '';
    const reference = `${departementId.toString().padStart(2, '0')} ${lotTechniqueId} ${code} ${rang}`;

    const prixUnitaire = parseFloat(String(articleData?.prix_unitaire ?? '')) || 0;
    const quantite = parseInt(String(articleData?.quantity ?? ''), 10) || 1;
    const seuilA = catalogue?.seuil_a || 5;
    const seuilB = catalogue?.seuil_b || 10;

    return {
      id: articleData?.id?.toString() || '',
      catalogueId: catalogue?.id?.toString() || '',
      code: reference,
      nom: catalogue?.nom || 'Article sans nom',
      description: catalogue?.description || '',
      prix: prixUnitaire,
      unite: catalogue?.unite || 'Unité',
      quantite,
      totalHT: prixUnitaire * quantite,
      prixDegressifs: {
        palier1: { min: 1, max: seuilA, prix: prixUnitaire },
        palier2: { min: seuilA + 1, max: seuilB, prix: prixUnitaire },
        palier3: { min: seuilB + 1, prix: prixUnitaire },
      },
    };
  });
};

export const totalArticlesHT = (articles: Article[]): number =>
  articles.reduce((sum, a) => sum + a.totalHT, 0);

// --- Lecture seule ------------------------------------------------------------
// Editable uniquement en brouillon acheteur et non expire (acheteur-only).
export const isConsultationSectionReadOnly = (operation: OperationBC, expired: boolean): boolean =>
  expired || !isBrouillon(operation);

// --- Statut de consultation (encart "lancee / transferee") --------------------
export interface ConsultationStatusView {
  type: 'transferee' | 'none';
  isVisible: boolean;
  showOffreEnvoyee: boolean;
}

const HIDDEN: ConsultationStatusView = { type: 'none', isVisible: false, showOffreEnvoyee: false };

export const computeConsultationStatus = (operation: OperationBC): ConsultationStatusView => {
  const commandes = operation.commandes || [];
  const sid = operation.status?.id;

  if (operation.transferred === true) {
    // Apres refus du rang 1 en phase d'analyse : ne rien afficher (rang suivant pas lance).
    if (sid === StatusId.AAnalyser) {
      const rang1 = commandes.find((c) => parseRang(c.rang) === 1);
      if (rang1 && rang1.id_status === StatusId.Refuse) {
        return HIDDEN;
      }
    }

    // Un rang est effectivement en cours cote prestataire : AChiffrer(11) ou AttenteAnalyse(13).
    // Valide(7) inclus pour que la banniere "Consultation lancee au rang X" PERSISTE apres
    // acceptation/signature du bon de commande : sinon le rang quitte AChiffrer/AttenteAnalyse et
    // l'encart disparait de l'onglet Consultation.
    const rangEnCours = commandes.find(
      (c) =>
        c.id_status === StatusId.AChiffrer ||
        c.id_status === StatusId.AttenteAnalyse ||
        c.id_status === StatusId.Valide,
    );
    if (rangEnCours) {
      return {
        type: 'transferee',
        isVisible: true,
        // "Offre envoyee par le prestataire" : statut PropositionFaite(15).
        // (statut ambigu cote DB -> a confirmer.)
        showOffreEnvoyee: sid === StatusId.PropositionFaite,
      };
    }
    return HIDDEN;
  }

  return HIDDEN;
};

// --- Validation du formulaire -------------------------------------------------
export interface ConsultationValidation {
  valid: boolean;
  errors: Record<string, string>;
}

export const validateConsultationForm = (formulaire?: OperationFormulaire): ConsultationValidation => {
  const f = formulaire || {};
  const errors: Record<string, string> = {};

  // Champs obligatoires : STRICTEMENT alignes sur MS (validateConsultationForm) pour ce qu'on a copie
  // dans BC. Hors perimetre BC : l'attestation BPU et les dates procedurales du MS (ouverture des plis,
  // notification, date limite de reception) — le bon de commande a une duree fixe de 10 jours.
  if (!f.nomOuvrage?.trim()) errors.nomOuvrage = "Le nom de l'ouvrage est obligatoire";
  if (!f.nomOperation?.trim()) errors.nomOperation = "Le nom de l'opération est obligatoire";
  // Adresse : elle ne vaut que si elle a ete choisie dans les suggestions IGN, ce que prouve la
  // presence des champs DERIVES (code postal, ville, pays). Ils ne sont plus affiches : leur erreur
  // doit donc remonter sur le champ Adresse, seul visible — sinon le lancement echoue sur un champ
  // que personne ne peut voir ni corriger.
  if (!f.adresse?.trim()) {
    errors.adresse = "L'adresse est obligatoire";
  } else if (!f.codePostal?.trim() || !f.ville?.trim() || !f.pays?.trim()) {
    errors.adresse = "Sélectionnez l'adresse dans la liste de suggestions pour valider la localisation";
  }
  if (!f.besoin?.trim()) errors.besoin = 'Le cahier des charges est obligatoire';
  // Contact operationnel : seul le NOM est obligatoire — c'est l'interlocuteur que le titulaire
  // devra joindre. Fonction, mail et telephone restent facultatifs, mais s'ils sont renseignes leur
  // FORMAT est verifie : un mail ou un numero errone sur un document contractuel ne se voit qu'au
  // moment ou quelqu'un essaie de s'en servir.
  if (!f.contactNom?.trim()) errors.contactNom = 'Le nom du contact opérationnel est obligatoire';
  if (f.contactMail?.trim() && !isValidEmail(f.contactMail)) errors.contactMail = FORMAT_ERROR_MAIL;
  if (f.contactTelephone?.trim() && !isValidPhoneFr(f.contactTelephone)) {
    errors.contactTelephone = FORMAT_ERROR_TELEPHONE;
  }
  // Particularites administratives (les 4 premieres sont requises cote MS ; charte + clause : non).
  if (!f.niveauSensibilite?.trim()) errors.niveauSensibilite = 'Le niveau de sensibilité est obligatoire';
  if (!f.conditionVisite?.trim()) errors.conditionVisite = 'Le champ « Visite préalable obligatoire » est requis';
  if (!f.verificationIdentites?.trim()) errors.verificationIdentites = 'La vérification préalable des identités est obligatoire';
  if (!f.habilitation?.trim()) errors.habilitation = "L'habilitation particulière est obligatoire";
  // Dates d'operation (marquees * dans l'UI ; pre-remplies mais requises avant lancement/transfert).
  if (!f.dateDebutOperation) errors.dateDebutOperation = 'La date prévisionnelle de démarrage est obligatoire';
  if (!f.delaisFinOperation || Number(f.delaisFinOperation) < 10) {
    errors.delaisFinOperation = 'La durée prévisionnelle est obligatoire';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// --- Corps de sauvegarde (PUT /bdc/operation/{id}) ----------------------------
const formatDateForApi = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const addDays = (base: Date, days: number): Date => {
  const d = new Date(base);
  d.setDate(base.getDate() + days);
  return d;
};

// Reconstruit le payload de sauvegarde avec les memes fallbacks de dates que l'ancienne
// feuille. Les libelles de coordonnees suivent le swap volontaire cote API BC.
export const buildConsultationSaveBody = (operation: OperationBC): Record<string, unknown> => {
  // Source = la vue FUSIONNEE (valeurs persistees + edition en cours), celle que l'ecran affiche —
  // et non `operation.formulaire`, qui ne contient QUE les champs touches depuis le chargement.
  // Avec le buffer seul, tout champ deja enregistre mais non retouche partait a `null` et ECRASAIT
  // en base la valeur encore visible a l'ecran (nom de l'ouvrage, pays, doc technique...).
  const f = buildConsultationFormData(operation);

  const dateDebutConsultation =
    formatDateForApi(f.dateDebutConsultation as string | undefined) ||
    new Date().toISOString().split('T')[0];

  const dateFinConsultation =
    formatDateForApi(f.dateFinConsultation as string | undefined) ||
    addDays(new Date(dateDebutConsultation), 10).toISOString().split('T')[0];

  const dateDebutOperation =
    formatDateForApi(f.dateDebutOperation) ||
    addDays(new Date(dateFinConsultation), 1).toISOString().split('T')[0];

  const delai = (f.delaisFinOperation as number | undefined) || 30;
  const dateFinOperation =
    formatDateForApi(f.dateFinOperation) ||
    addDays(new Date(dateDebutOperation), Number(delai)).toISOString().split('T')[0];

  return {
    nom: f.nomOperation?.trim() || '',
    reference: f.referenceInterne?.trim() || '',
    besoin: f.besoin?.trim() || '',
    adresse: f.adresse?.trim() || '',
    code_postal: f.codePostal?.trim() || '',
    ville: f.ville?.trim() || '',
    // `facturation` porte la MODALITE de facturation. La colonne `modalite_facturation`,
    // ajoutee le 2026-07-13 par la parite du descriptif MS, faisait doublon avec elle et a
    // ete supprimee : le BC a toujours porte cette information dans `facturation`, c'est
    // elle que le document contractuel imprime.
    facturation: f.modaliteFacturation?.trim() || '',
    coordonnees_maitrise_ouvrage: f.coordonneesMaitrise?.trim() || '',
    condition_visite: f.conditionVisite?.trim() || '',
    // La visite est desormais saisie via un unique champ texte (Particularites, parite MS) porte par
    // conditionVisite. Le booleen `visite` en est derive : vide ou "Non..." -> 0, sinon 1.
    visite:
      f.conditionVisite && f.conditionVisite.trim() && !/^non/i.test(f.conditionVisite.trim())
        ? '1'
        : '0',
    date_debut_operation: dateDebutOperation,
    date_fin_operation: dateFinOperation,
    date_debut_consultation: dateDebutConsultation,
    date_fin_consultation: dateFinConsultation,
    delai_execution: delai,
    // Descriptif refonte (parite MS) : camelCase form -> snake_case colonnes. `?? null` pour
    // permettre l'effacement d'un champ (envoyer null plutot que d'omettre la cle).
    nom_ouvrage: f.nomOuvrage?.trim() || null,
    // Contact operationnel de l'operation. N'ecrit JAMAIS sur le compte acheteur ni sur l'organisme :
    // ces quatre colonnes vivent sur `operation`.
    contact_nom: f.contactNom?.trim() || null,
    contact_fonction: f.contactFonction?.trim() || null,
    contact_mail: f.contactMail?.trim() || null,
    contact_telephone: f.contactTelephone?.trim() || null,
    pays: f.pays?.trim() || null,
    moe_interlocuteur: f.moeInterlocuteur?.trim() || null,
    moe_mail: f.moeMail?.trim() || null,
    moe_telephone: f.moeTelephone?.trim() || null,
    niveau_sensibilite: f.niveauSensibilite?.trim() || null,
    signataire_commande: f.signataire?.trim() || null,
    phases: f.phases?.trim() || null,
    verification_identites: f.verificationIdentites?.trim() || null,
    habilitation: f.habilitation?.trim() || null,
    charte_reglementation: f.charteReglementation?.trim() || null,
    clause_suivi_environnemental: f.clauseSuiviEnvironnemental?.trim() || null,
    url_doc_technique: f.urlDocTechnique?.trim() || null,
    modalite_acces_documentation: f.modaliteAccesDocumentation?.trim() || null,
    liste_pieces_documentation: f.listePiecesDocumentation?.trim() || null,
  };
};

// --- formData enrichi pour OperationFormulaireAdmin (editable) ------------------
// Champs lus depuis l'operation (snake_case) vers le form (camelCase).
// Sert de socle pre-rempli : les editions en cours (buffer `formulaire`) le recouvrent.
//
// La couverture doit rester EXHAUSTIVE vis-a-vis de buildConsultationSaveBody. `formulaire` n'est
// jamais hydrate depuis l'API : c'est un buffer purement local, vide au chargement, qui ne contient
// que les champs touches depuis l'ouverture de la page. Tout champ du payload absent d'ici est donc
// invisible a la reouverture d'un brouillon, ET reparti a vide au premier enregistrement — le module
// ecrit `$operation->update($request->all())` sans filtrer les valeurs vides.
// Ajouter une cle au payload impose d'ajouter sa lecture ici.
const refonteFromOperation = (operation: OperationBC): Partial<OperationFormulaire> => ({
  // Bloc "operation" : ces 13 champs manquaient, alors qu'ils sont tous ecrits par le payload.
  nomOperation: operation.nom as string | undefined,
  referenceInterne: operation.reference as string | undefined,
  besoin: operation.besoin as string | undefined,
  adresse: operation.adresse as string | undefined,
  codePostal: operation.code_postal as string | undefined,
  ville: operation.ville as string | undefined,
  // Swap volontaire cote API BC, deja documente dans buildConsultationSaveBody : le form parle de
  // `facturation` n'alimente plus "maitrise d'ouvrage" : elle porte desormais la seule
  // modalite de facturation, lue plus bas dans `modaliteFacturation`. Le croisement
  // historique disparait avec la colonne `modalite_facturation` qui le doublonnait.
  coordonneesMaitrise: operation.coordonnees_maitrise_ouvrage as string | undefined,
  conditionVisite: operation.condition_visite as string | undefined,
  dateDebutConsultation: operation.date_debut_consultation as string | undefined,
  dateFinConsultation: operation.date_fin_consultation as string | undefined,
  dateDebutOperation: operation.date_debut_operation as string | undefined,
  dateFinOperation: operation.date_fin_operation as string | undefined,

  // Contact operationnel : valeur SAISIE sur l'operation, sinon repli sur l'acheteur qui l'a initiee.
  //
  // Le repli vit ICI, dans le socle du formulaire, et non dans l'affichage : `buildConsultationSaveBody`
  // lit cette meme vue fusionnee, donc le contact pre-rempli est PERSISTE des le premier
  // enregistrement. Pose uniquement a l'affichage, il serait reste cosmetique — le piege deja
  // rencontre avec le niveau de sensibilite.
  // Repli en `||` et non `??` : une colonne VIDE vaut absente. Un champ laisse vide doit afficher le
  // createur, jamais un blanc — c'est la regle pour les quatre champs, sans exception.
  // Le buffer de saisie, lui, garde la priorite (fusion `{...refonte, ...base}`) : effacer un champ
  // pour le retaper ne doit pas le voir se re-remplir sous les doigts.
  contactNom:
    operation.contact_nom ||
    [operation.acheteur?.prenom, operation.acheteur?.nom].filter(Boolean).join(' '),
  contactFonction: operation.contact_fonction || operation.acheteur?.fonction,
  contactMail: operation.contact_mail || operation.acheteur?.email_contact,
  contactTelephone: operation.contact_telephone || operation.acheteur?.telephone,

  nomOuvrage: operation.nom_ouvrage,
  pays: operation.pays,
  moeInterlocuteur: operation.moe_interlocuteur,
  moeMail: operation.moe_mail,
  moeTelephone: operation.moe_telephone,
  // Defaut "Public (P)", comme le MS : une operation sans niveau saisi pre-selectionne Public dans
  // le select (option en tete, identique cote BC et MS) plutot que de laisser le champ vide.
  niveauSensibilite: operation.niveau_sensibilite ?? 'Public (P)',
  signataire: operation.signataire_commande,
  phases: operation.phases,
  verificationIdentites: operation.verification_identites,
  habilitation: operation.habilitation,
  charteReglementation: operation.charte_reglementation,
  clauseSuiviEnvironnemental: operation.clause_suivi_environnemental,
  modaliteFacturation: operation.facturation,
  urlDocTechnique: operation.url_doc_technique,
  modaliteAccesDocumentation: operation.modalite_acces_documentation,
  listePiecesDocumentation: operation.liste_pieces_documentation,
});

export const buildConsultationFormData = (operation: OperationBC): OperationFormulaire => {
  const base = operation.formulaire || {};
  const refonte = refonteFromOperation(operation);
  if (base.delaisFinOperation) {
    return { ...refonte, ...base, delaisConsultation: 10 };
  }

  let delaisFinOperation: number = 30;
  if (operation.delai_execution) {
    delaisFinOperation = Number(operation.delai_execution);
  } else if (operation.date_debut_operation && operation.date_fin_operation) {
    const debut = new Date(operation.date_debut_operation);
    const fin = new Date(operation.date_fin_operation);
    const diff = Math.abs(fin.getTime() - debut.getTime());
    delaisFinOperation = Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  return { ...refonte, ...base, delaisConsultation: 10, delaisFinOperation };
};
